import { useCallback, useEffect, useMemo, useState } from "react";
import {
  acceptFriendRequest,
  fetchFriends,
  removeFriend,
  sendFriendRequest,
  type Session,
} from "../lib/api";
import type { FriendLink } from "../../shared/types";

export function useFriends(session: Session | null) {
  const [friends, setFriends] = useState<FriendLink[]>([]);

  const loadFriends = useCallback(async () => {
    if (!session) return;
    const list = await fetchFriends(session);
    setFriends(list);
  }, [session]);

  useEffect(() => {
    void loadFriends();
  }, [loadFriends]);

  const sendRequest = useCallback(
    async (friendAddress: string) => {
      if (!session) return;
      try {
        await sendFriendRequest(session, friendAddress);
      } finally {
        await loadFriends();
      }
    },
    [session, loadFriends]
  );

  const acceptRequest = useCallback(
    async (friend: FriendLink) => {
      if (!session) return;
      const other =
        friend.userAddress === session.address
          ? friend.friendAddress
          : friend.userAddress;
      await acceptFriendRequest(session, other);
      await loadFriends();
    },
    [session, loadFriends]
  );

  const accepted = useMemo(
    () => friends.filter((f) => f.status === "accepted"),
    [friends]
  );

  const pending = useMemo(
    () =>
      friends.filter(
        (f) => f.status === "pending" && f.friendAddress === session?.address
      ),
    [friends, session?.address]
  );

  const removeRequest = useCallback(
    async (friend: FriendLink) => {
      if (!session) return;
      const other =
        friend.userAddress === session.address
          ? friend.friendAddress
          : friend.userAddress;
      await removeFriend(session, other);
      await loadFriends();
    },
    [session, loadFriends]
  );

  return {
    friends,
    accepted,
    pending,
    sendRequest,
    removeRequest,
    acceptRequest,
    loadFriends,
  };
}
